import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { getAllBlogPosts } from '../../lib/blogs.js';
import { getAllDisciplines } from '../../lib/disciplines.js';
import { getAllEvents } from '../../lib/events.js';
import { getAllProjects } from '../../lib/projects.js';
import {
  DEFAULT_DESCRIPTION,
  NOT_FOUND_TITLE,
  absoluteUrl,
  buildRoutes,
  findRoute,
} from '../../lib/seo.js';

function upsert(tagName, attr, key, valueAttr, value) {
  let el = document.head.querySelector(`${tagName}[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement(tagName);
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute(valueAttr, value);
}

function removeTag(selector) {
  const el = document.head.querySelector(selector);
  if (el) el.remove();
}

/**
 * Keeps <title>, meta description, canonical URL, Open Graph / Twitter tags and
 * the robots directive in sync with the current route. Renders nothing.
 * Mount once, after <Routes>, so it runs last and wins over page-level hooks.
 */
export default function RouteSeo() {
  const { pathname } = useLocation();

  const routes = useMemo(
    () =>
      buildRoutes({
        disciplines: getAllDisciplines(),
        posts: getAllBlogPosts(),
        events: getAllEvents(),
        projects: getAllProjects(),
      }),
    [],
  );

  const route = findRoute(routes, pathname);

  useEffect(() => {
    const title = route ? route.title : NOT_FOUND_TITLE;
    const description = route ? route.description : DEFAULT_DESCRIPTION;

    document.title = title;
    upsert('meta', 'name', 'description', 'content', description);

    upsert('meta', 'property', 'og:title', 'content', title);
    upsert('meta', 'property', 'og:description', 'content', description);
    upsert('meta', 'name', 'twitter:title', 'content', title);
    upsert('meta', 'name', 'twitter:description', 'content', description);

    if (route) {
      const url = absoluteUrl(route.path);
      upsert('link', 'rel', 'canonical', 'href', url);
      upsert('meta', 'property', 'og:url', 'content', url);
      upsert('meta', 'name', 'twitter:url', 'content', url);
      removeTag('meta[name="robots"]');
    } else {
      // Unknown URL: never let it be indexed or declared canonical.
      removeTag('link[rel="canonical"]');
      upsert('meta', 'name', 'robots', 'content', 'noindex');
    }
  }, [route]);

  return null;
}
